import { useId, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import clsx from "clsx";

import { errorMessage } from "../lib/api";
import { placeLine, placeSourceNote } from "../lib/format";
import { usePatchEntry } from "../lib/queries";
import type { Entry } from "../lib/types";
import { useToasts } from "./Toasts";

export type PlaceEditorProps = {
  entry: Entry;
  className?: string;
};

/**
 * Where the find was made — and, because this is the single most confusing
 * thing about Foundling, an explanation of why that matters.
 *
 * A find with no place joins no collection. It sits unfiled until a place is
 * set, at which point the server files it. Nobody guesses that on their own, so
 * we say it in one sentence, right where the fix is.
 */
export function PlaceEditor({ entry, className }: PlaceEditorProps) {
  const id = useId();
  const { push } = useToasts();
  const patch = usePatchEntry(entry.id);
  const current = placeLine(entry.place);
  const [draft, setDraft] = useState(current ?? "");
  const [editing, setEditing] = useState(current === null);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const query = draft.trim();
    if (query.length === 0 || query === current) {
      setEditing(false);
      return;
    }

    patch.mutate(
      { placeQuery: query },
      {
        onSuccess: (updated) => {
          setEditing(false);
          const primary = updated.collections.find((collection) => collection.isPrimary);
          const filed = primary ?? updated.collections[0];
          push({
            kind: "success",
            title: filed ? `Filed into ${filed.title}.` : `Place set to ${placeLine(updated.place) ?? query}.`,
            ...(filed
              ? { body: "Foundling has drawn up things to look for nearby." }
              : {
                  body: "No collection matched this one yet — it will join one as soon as a sibling find turns up.",
                }),
          });
        },
        onError: (error) =>
          push({
            kind: "problem",
            title: "That place didn't resolve.",
            body: errorMessage(error, "Try a town and a region, like “Madison, Wisconsin”."),
          }),
      },
    );
  };

  const clear = () => {
    patch.mutate(
      { placeQuery: "" },
      {
        onSuccess: () => {
          setDraft("");
          setEditing(true);
          push({ title: "Place removed. This find is unfiled again." });
        },
        onError: (error) =>
          push({ kind: "problem", title: "Couldn't clear the place.", body: errorMessage(error) }),
      },
    );
  };

  const unfiled = current === null;

  return (
    <section
      className={clsx(
        "sheet grain relative p-4 sm:p-5",
        unfiled && "border-l-2 border-l-ochre",
        className,
      )}
    >
      <div className="flex items-baseline justify-between gap-3">
        <h2 className="field-label text-ink">Where it was found</h2>
        {patch.isPending ? (
          <p role="status" aria-live="polite" className="field-label-tight text-ink-soft">
            Looking it up…
          </p>
        ) : null}
      </div>

      {!editing && current ? (
        <div className="mt-3">
          <p className="font-prose text-[1.0625rem] leading-snug text-ink">{current}</p>
          {placeSourceNote(entry.place) ? (
            <p className="mt-1 field-label-tight text-ink-soft">{placeSourceNote(entry.place)}</p>
          ) : null}

          {entry.collections.length > 0 ? (
            <ul className="mt-3 flex flex-wrap gap-2">
              {entry.collections.map((collection) => (
                <li key={collection.id}>
                  <Link
                    to={`/collections/${collection.id}`}
                    className="inline-flex items-center gap-1.5 border border-teal/40 bg-[color-mix(in_oklab,var(--color-teal)_8%,transparent)] px-2 py-1 font-label text-xs tracking-wide text-teal hover:bg-[color-mix(in_oklab,var(--color-teal)_16%,transparent)]"
                  >
                    {collection.title}
                    {collection.isPrimary ? <span className="sr-only"> (primary collection)</span> : null}
                  </Link>
                </li>
              ))}
            </ul>
          ) : null}

          <div className="mt-4 flex flex-wrap items-center gap-4">
            <button
              type="button"
              onClick={() => setEditing(true)}
              className="link touch font-label text-sm"
            >
              Correct the place
            </button>
            <button
              type="button"
              onClick={clear}
              disabled={patch.isPending}
              className="touch font-label text-sm text-ink-soft underline decoration-rule underline-offset-4 hover:text-ink disabled:opacity-60"
            >
              Remove it
            </button>
          </div>
        </div>
      ) : (
        <form onSubmit={submit} className="mt-3">
          {unfiled ? (
            <p className="font-prose text-[1.0625rem] leading-relaxed text-ink">
              <span className="text-ochre">Unfiled.</span> Add a place to file it into a collection.
            </p>
          ) : null}

          <label htmlFor={id} className="field-label mt-4 block text-ink-soft">
            Town, region
          </label>
          <div className="mt-1.5 flex flex-wrap items-stretch gap-2">
            <input
              id={id}
              type="text"
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              placeholder="Madison, Wisconsin"
              autoComplete="address-level2"
              enterKeyHint="done"
              aria-describedby={`${id}-help`}
              className="min-w-0 flex-1 border border-rule bg-paper px-3 py-2.5 font-prose text-[1.0625rem] text-ink placeholder:text-ink-soft/60 focus:border-ochre focus:outline-none"
            />
            <button
              type="submit"
              disabled={patch.isPending || draft.trim().length === 0}
              className="touch shrink-0 border border-ink bg-ink px-4 py-2 font-label text-sm font-medium tracking-wide text-paper transition-opacity hover:opacity-90 disabled:opacity-40"
            >
              {patch.isPending ? "Filing…" : "File it"}
            </button>
            {current ? (
              <button
                type="button"
                onClick={() => {
                  setDraft(current);
                  setEditing(false);
                }}
                className="touch shrink-0 px-2 font-label text-sm text-ink-soft underline decoration-rule underline-offset-4 hover:text-ink"
              >
                Cancel
              </button>
            ) : null}
          </div>
          <p id={`${id}-help`} className="mt-2 font-prose text-sm text-ink-soft">
            A town and a region is plenty.
          </p>
        </form>
      )}
    </section>
  );
}
